import { Badge } from "@/components/ui/badge";
import { formatCurrencyEUR, formatRelative } from "@/lib/utils";
import {
  MDF_REQUEST_STATUS_LABEL,
  VIEW_COLUMN_LABEL,
  type MdfRequestStatus,
} from "@colab/shared";
import type { AdminRequestQueueRow } from "./types";

// Phase 7 sub-bucket 19 — refactored to a column-key-driven render map with
// `visibleColumns?: string[]` for Saved-Views-driven column control. Default
// preserves the sub-bucket 15 column layout (the queue-page uses
// `showFundColumn` to opt into the Fund column).

const STATUS_VARIANT: Record<
  string,
  "default" | "outline" | "secondary" | "destructive"
> = {
  pending: "outline",
  approved: "default",
  spent: "secondary",
  declined: "destructive",
  cancelled: "outline",
};

function statusLabel(status: string): string {
  if (status in MDF_REQUEST_STATUS_LABEL) {
    return MDF_REQUEST_STATUS_LABEL[status as MdfRequestStatus];
  }
  // Bucket 9 added 'cancelled' but the legacy enum + LABEL map don't carry it.
  if (status === "cancelled") return "Cancelled";
  return status;
}

type ColumnDef = {
  key: string;
  align?: "left" | "right";
  render: (row: AdminRequestQueueRow) => React.ReactNode;
};

// Catalog keys match `VIEW_COLUMN_CATALOG.mdf_request` in segments-meta.
const COLUMNS: Record<string, ColumnDef> = {
  created_at: {
    key: "created_at",
    render: (r) => (
      <span className="whitespace-nowrap text-xs text-muted-foreground">
        {formatRelative(r.created_at)}
      </span>
    ),
  },
  partner: {
    key: "partner",
    render: (r) => <span className="font-medium">{r.partner_name}</span>,
  },
  fund: {
    key: "fund",
    render: (r) => (
      <span className="text-sm text-muted-foreground">{r.fund_name}</span>
    ),
  },
  campaign_name: {
    key: "campaign_name",
    render: (r) => (
      <>
        <div className="text-sm">{r.campaign_name}</div>
        {r.campaign_type ? (
          <div className="text-[11px] text-muted-foreground">
            {r.campaign_type}
          </div>
        ) : null}
      </>
    ),
  },
  amount_eur: {
    key: "amount_eur",
    align: "right",
    render: (r) => (
      <span className="tabular-nums">{formatCurrencyEUR(r.amount_eur)}</span>
    ),
  },
  status: {
    key: "status",
    render: (r) => (
      <Badge variant={STATUS_VARIANT[r.status] ?? "outline"}>
        {statusLabel(r.status)}
      </Badge>
    ),
  },
  // `approved_at` is in the SEGMENT_FIELDS.mdf_request catalog (date) and
  // VIEW_COLUMN_CATALOG.mdf_request (column) but not yet exposed on the
  // queue row shape — listAdminRequests() only selects created_at +
  // updated_at today. Leaving the column key out of the render map means
  // a saved view that toggles `approved_at` visible renders an empty header
  // for now; a future sub-bucket can extend the row shape + service select.
  campaign_type: {
    key: "campaign_type",
    render: (r) => (
      <span className="text-xs text-muted-foreground">
        {r.campaign_type ?? "—"}
      </span>
    ),
  },
};

const LEGACY_NO_FUND: readonly string[] = [
  "created_at",
  "partner",
  "campaign_name",
  "amount_eur",
  "status",
];
const LEGACY_WITH_FUND: readonly string[] = [
  "created_at",
  "partner",
  "fund",
  "campaign_name",
  "amount_eur",
  "status",
];

export function MdfRequestsTable({
  rows,
  onSelect,
  showFundColumn = false,
  selectedId,
  visibleColumns,
}: {
  rows: AdminRequestQueueRow[];
  onSelect: (id: string) => void;
  showFundColumn?: boolean;
  selectedId?: string | null;
  /** Saved-view-driven column visibility + ordering. Falls back to the
   *  legacy full set when omitted. */
  visibleColumns?: string[];
}) {
  const cols = (visibleColumns && visibleColumns.length > 0
    ? visibleColumns
    : showFundColumn
      ? LEGACY_WITH_FUND
      : LEGACY_NO_FUND
  )
    .map((k) => COLUMNS[k])
    .filter((c): c is ColumnDef => Boolean(c));

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {cols.map((c) => (
              <th
                key={c.key}
                className={`px-4 py-2.5 ${
                  c.align === "right" ? "text-right" : "text-left"
                }`}
              >
                {VIEW_COLUMN_LABEL[c.key] ?? c.key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {rows.map((r) => (
            <tr
              key={r.id}
              className={`cursor-pointer transition-colors hover:bg-muted/30 ${
                selectedId === r.id ? "bg-muted/40" : ""
              }`}
              onClick={() => onSelect(r.id)}
            >
              {cols.map((c) => (
                <td
                  key={c.key}
                  className={`px-4 py-3 ${
                    c.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
